import { motion } from 'framer-motion';
import { CheckCircle, AlertTriangle, XCircle, Clock, ArrowRight, Bell, ExternalLink } from 'lucide-react';
import StaticPageLayout from '../../components/StaticPageLayout';
import { useThemeStore } from '../../store/theme';

const services = [
  { name: 'Web Application', status: 'operational', uptime: '99.99%' },
  { name: 'API', status: 'operational', uptime: '99.98%' },
  { name: 'AI Chat Services', status: 'operational', uptime: '99.95%' },
  { name: 'Brain AI', status: 'operational', uptime: '99.97%' },
  { name: 'Task Automation', status: 'operational', uptime: '99.96%' },
  { name: 'Integrations', status: 'operational', uptime: '99.94%' },
  { name: 'Payment Processing', status: 'operational', uptime: '99.99%' },
  { name: 'Email Delivery', status: 'operational', uptime: '99.98%' },
];

const incidents = [
  {
    date: '2024-11-28',
    title: 'Scheduled Maintenance Completed',
    status: 'resolved',
    description:
      'Planned maintenance for database optimization completed successfully. All services are now operational.',
  },
  {
    date: '2024-11-15',
    title: 'API Latency Issues',
    status: 'resolved',
    description:
      'Some users experienced elevated API response times. The issue was identified and resolved within 45 minutes.',
  },
  {
    date: '2024-10-30',
    title: 'Payment Processing Delay',
    status: 'resolved',
    description:
      'Brief delay in payment processing due to third-party provider issues. All transactions were eventually processed successfully.',
  },
];

const uptimeHistory = [
  { date: 'Nov 30', status: 'up' },
  { date: 'Nov 29', status: 'up' },
  { date: 'Nov 28', status: 'maintenance' },
  { date: 'Nov 27', status: 'up' },
  { date: 'Nov 26', status: 'up' },
  { date: 'Nov 25', status: 'up' },
  { date: 'Nov 24', status: 'up' },
  { date: 'Nov 23', status: 'up' },
  { date: 'Nov 22', status: 'up' },
  { date: 'Nov 21', status: 'up' },
  { date: 'Nov 20', status: 'up' },
  { date: 'Nov 19', status: 'up' },
  { date: 'Nov 18', status: 'up' },
  { date: 'Nov 17', status: 'up' },
  { date: 'Nov 16', status: 'up' },
  { date: 'Nov 15', status: 'degraded' },
  { date: 'Nov 14', status: 'up' },
  { date: 'Nov 13', status: 'up' },
  { date: 'Nov 12', status: 'up' },
  { date: 'Nov 11', status: 'up' },
  { date: 'Nov 10', status: 'up' },
  { date: 'Nov 09', status: 'up' },
  { date: 'Nov 08', status: 'up' },
  { date: 'Nov 07', status: 'up' },
  { date: 'Nov 06', status: 'up' },
  { date: 'Nov 05', status: 'up' },
  { date: 'Nov 04', status: 'up' },
  { date: 'Nov 03', status: 'up' },
  { date: 'Nov 02', status: 'up' },
  { date: 'Nov 01', status: 'up' },
];

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'operational':
    case 'resolved':
      return <CheckCircle className="w-5 h-5 text-green-400" />;
    case 'degraded':
    case 'investigating':
      return <AlertTriangle className="w-5 h-5 text-yellow-400" />;
    case 'outage':
      return <XCircle className="w-5 h-5 text-red-400" />;
    default:
      return <Clock className="w-5 h-5 text-gray-400" />;
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'up':
      return 'bg-green-500';
    case 'degraded':
      return 'bg-yellow-500';
    case 'maintenance':
      return 'bg-blue-500';
    case 'down':
      return 'bg-red-500';
    default:
      return 'bg-gray-500';
  }
};

export default function Status() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const allOperational = services.every((s) => s.status === 'operational');

  return (
    <StaticPageLayout
      title="System Status"
      subtitle="Real-time status of all SweetPilot services and infrastructure."
    >
      {/* Overall Status */}
      <section className="mb-12">
        <div
          className={`p-8 rounded-2xl border text-center ${
            allOperational
              ? isDark ? 'bg-green-500/10 border-green-500/20' : 'bg-green-50 border-green-200'
              : isDark ? 'bg-yellow-500/10 border-yellow-500/20' : 'bg-yellow-50 border-yellow-200'
          }`}
        >
          {allOperational ? (
            <>
              <CheckCircle className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
              <h2 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>All Systems Operational</h2>
              <p className={isDark ? 'text-gray-400' : 'text-slate-600'}>
                All services are running smoothly. No issues detected.
              </p>
            </>
          ) : (
            <>
              <AlertTriangle className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`} />
              <h2 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>Partial Service Disruption</h2>
              <p className={isDark ? 'text-gray-400' : 'text-slate-600'}>Some services may be experiencing issues.</p>
            </>
          )}
        </div>
      </section>

      {/* Uptime History */}
      <section className="mb-12">
        <div className="flex items-center justify-between mb-4">
          <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>30-Day Uptime History</h2>
          <span className={`text-sm font-medium ${isDark ? 'text-green-400' : 'text-green-600'}`}>99.97% uptime</span>
        </div>
        <div className="flex gap-1">
          {uptimeHistory.map((day, index) => (
            <div
              key={index}
              className={`flex-1 h-10 rounded ${getStatusColor(day.status)} opacity-80 hover:opacity-100 transition-opacity cursor-pointer group relative`}
              title={`${day.date}: ${day.status}`}
            >
              <div className={`absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 rounded text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none ${
                isDark ? 'bg-dark-800 text-white' : 'bg-slate-800 text-white'
              }`}>
                {day.date}: {day.status}
              </div>
            </div>
          ))}
        </div>
        <div className={`flex justify-between mt-2 text-xs ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>
          <span>30 days ago</span>
          <span>Today</span>
        </div>
      </section>

      {/* Services Status */}
      <section className="mb-12">
        <h2 className={`text-xl font-bold mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}>Service Status</h2>
        <div className="space-y-3">
          {services.map((service, index) => (
            <motion.div
              key={service.name}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
              className={`flex items-center justify-between p-4 rounded-xl border ${
                isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'
              }`}
            >
              <div className="flex items-center gap-3">
                {getStatusIcon(service.status)}
                <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{service.name}</span>
              </div>
              <div className="flex items-center gap-4">
                <span className={`text-sm ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>{service.uptime} uptime</span>
                <span className={`px-3 py-1 rounded-full text-xs capitalize ${
                  isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-700'
                }`}>
                  {service.status}
                </span>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Recent Incidents */}
      <section className="mb-12">
        <h2 className={`text-xl font-bold mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}>Recent Incidents</h2>
        <div className="space-y-4">
          {incidents.map((incident, index) => (
            <motion.div
              key={incident.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`p-6 rounded-2xl border ${
                isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  {getStatusIcon(incident.status)}
                  <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{incident.title}</h3>
                </div>
                <span className={`text-sm ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>{incident.date}</span>
              </div>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>{incident.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Subscribe */}
      <section className={`text-center p-8 rounded-2xl border ${
        isDark
          ? 'bg-gradient-to-br from-purple-500/10 to-cyan-500/10 border-white/5'
          : 'bg-gradient-to-br from-purple-50 to-cyan-50 border-purple-100'
      }`}>
        <Bell className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
        <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Get Status Updates</h2>
        <p className={`mb-6 max-w-md mx-auto ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
          Subscribe to receive notifications about scheduled maintenance and service disruptions.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
          <input
            type="email"
            placeholder="Enter your email"
            className={`flex-1 px-4 py-3 border rounded-xl focus:outline-none focus:border-purple-500 ${
              isDark
                ? 'bg-dark-800 border-white/10 text-white placeholder-gray-500'
                : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400'
            }`}
          />
          <button className="px-6 py-3 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-xl font-medium text-white flex items-center justify-center gap-2">
            Subscribe <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>
    </StaticPageLayout>
  );
}
